import { useCurrentConsumerProject } from "@/lib/context/current-consumer-project-context";
import { useChat } from "@ai-sdk/react";
import {
  getCurrentChatSessionInfo,
  shouldContinueConversation,
  type ChatbotProps,
} from "@zapier/chatbots-tailwind";
import PoweredByZapier from "components/PoweredByZapier";
import cuid from "cuid";
import { format as formatDate } from "date-fns";
import { PROJECT_SESSION_ID_HEADER } from "lib/constants";
import { useEmbed } from "lib/context/embed-context";
import { usePageContext } from "lib/context/page-context";
import { useAccountPlan } from "lib/hooks/useAccountPlan";
import { useContentDatasource } from "lib/hooks/useContentDatasource";
import { getProjectSessionId } from "lib/project-sessions/project-session-storage";
import { useInterfacesTheme } from "lib/theme/ThemeProvider";
import { useTrackingContext } from "observability";
import Markdown from "react-markdown";
import ReactTextareaAutosize from "react-textarea-autosize";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { cn } from "utils/cn";
import { trpc } from "utils/trpc";
import { v4 as uuid } from "uuid";
import { ChatbotEmbedConfigSchema, type ChatbotBlock } from "../types";
import { useSetChatbot } from "./ChatbotContext";
import { handleChatbotError } from "./handleChatbotError";
import { handleChatbotEvent } from "./handleChatbotEvent";

export function useChatbotTailwindProps({ block }: { block: ChatbotBlock }) {
  const setChatbot = useSetChatbot();

  const { isEditing, isEmbedded, projectId, pageId } = usePageContext();
  const blockId = block.config.id;

  const project = useCurrentConsumerProject();
  const interfacesTheme = useInterfacesTheme();

  const { isPopup, noBackground } = useEmbed();

  const { data: contentDatasource } = useContentDatasource();

  const suggestMutation = trpc.chatbots.suggest.useMutation();
  const { mutateAsync: initChatbot } = trpc.chatbots.init.useMutation();
  const { mutateAsync: createChatbotEvent } =
    trpc.chatbots.createEvent.useMutation();

  const {
    emitChatbotAITokenUsageReceivedEvent,
    emitAiTrackingPredictionUserFeedbackEvent,
    emitConsumerInteractionEvent,
    emitChatbotsConsumerInteractionEvent,
  } = useTrackingContext();

  const { isFree } = useAccountPlan(project.creator);

  const interfacesSessionId = getProjectSessionId();

  const chatbotTailwindProps: Omit<
    ChatbotProps,
    "loadingSlot" | "disabledSlot"
  > = {
    chatEndpoint: "/api/chat",
    chatbotId: block.config.chatbotId,
    className: cn({
      "my-0.5": !isPopup,
      "mt-0.5": isPopup,
      "!my-0 mx-auto !h-full max-h-svh grow [&_ul]:!pt-4": project.isHidden,
      "min-h-full": !project.isHidden,
      "[&_fieldset]:!mb-4": project.isHidden && project.hideZapierButton,
      "[&_fieldset]:!mb-0": project.isHidden && !project.hideZapierButton,
    }),
    embedConfig: ChatbotEmbedConfigSchema.parse(block.config),
    extraChatBodyData: {
      blockId: block.config.id,
      mode: isEditing ? "preview" : undefined,
      params: contentDatasource,
    },
    extraChatHeaders: interfacesSessionId
      ? {
          [PROJECT_SESSION_ID_HEADER]: interfacesSessionId,
        }
      : {},
    initialMessages: [],
    isBuilder: false,
    isEditing,
    isEmbedded,
    isPopup,
    isPreview: project.isHidden,
    noBackground,
    projectIsHidden: project.isHidden,
    onError: handleChatbotError,
    async onEvent(payload, context) {
      return handleChatbotEvent({
        payload,
        context,
        project,
        createChatbotEvent,
        emitChatbotAITokenUsageReceivedEvent,
        emitAiTrackingPredictionUserFeedbackEvent,
        emitConsumerInteractionEvent,
        emitChatbotsConsumerInteractionEvent,
        projectId,
        pageId,
        blockId,
        isEmbedded,
        isFree: isFree(),
      });
    },
    async init(chatbotId, options) {
      // make a utility variable for if we're restarting
      const isRestarting = typeof options == "object" && options?.isRestart;

      // if we're not restarting, determine if we have an existing chat session
      // that we should continue
      let chatbotSessionId: string | undefined;

      if (!isRestarting && shouldContinueConversation(chatbotId)) {
        const currentChatSessionInfo = getCurrentChatSessionInfo(chatbotId);
        chatbotSessionId = currentChatSessionInfo?.sessionId;
      }

      // initialize the chatbot (either starting a new chat session or continuing
      // an existing one)
      try {
        setChatbot({ type: "pending" });

        const initChatbotResponse = await initChatbot({
          chatbotId,
          configParams: contentDatasource,
          theme: interfacesTheme,
          chatbotSessionId,
        });

        setChatbot({ type: "resolved", initChatbotResponse });

        return initChatbotResponse as Awaited<ReturnType<ChatbotProps["init"]>>;
      } catch (error) {
        setChatbot({
          type: "rejected",
          error: error instanceof Error ? error : new Error(String(error)),
        });
        throw error;
      }
    },
    async suggest(chatbotId, sessionId) {
      return suggestMutation.mutateAsync({ chatbotId, sessionId });
    },
    renderPoweredByZapier() {
      return (
        <PoweredByZapier
          hasChatbot
          alignment="center"
          position="relative"
          variant={interfacesTheme?.mode === "Dark" ? "dark" : "light"}
          withinContainer
          occupySpaceWhenEmpty={!project.isHidden || isPopup}
        />
      );
    },

    // dependency injection
    cn,
    cuid,
    uuid,
    formatDate,
    Markdown,
    remarkGfm,
    rehypeHighlight,
    ReactTextareaAutosize,
    rehypeRaw,
    useChat,
  };

  return chatbotTailwindProps;
}
